import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroupsStore } from 'src/store/groups';
import { useAuthStore } from 'src/store/auth';
import { RoleType } from 'src/constants';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';

const Groups = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { items, loading, loadList, remove } = useGroupsStore();
  
  // Local search state
  const [search, setSearch] = useState('');

  // Check if user is admin, redirect to root if not
  useEffect(() => {
    if (currentUser && currentUser.role !== RoleType.Admin) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    groupId: string | number;
    x: number;
    y: number;
  }>({
    isOpen: false,
    groupId: '',
    x: 0,
    y: 0,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Delete confirmation state
  const [confirmOpen, setConfirmOpen] = useState<null | string | number>(null);


  useEffect(() => {
    loadList();
  }, [loadList]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Filter groups based on search
  const filteredGroups = items.filter(item => {
    if (item.type !== 1) return false;
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      item.name?.toLowerCase().includes(searchLower) ||
      item.host?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentGroups = filteredGroups.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleContextMenuClick = (e: React.MouseEvent, groupId: string | number) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      groupId,
      x: rect.right - 70,
      y: rect.bottom + 2,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isOpen: false,
      groupId: '',
      x: 0,
      y: 0,
    });
  };

  const handleViewGroup = (groupId: string | number) => {
    navigate(`/admin-groups/${groupId}`);
    handleCloseContextMenu();
  };

  const handleDeleteGroup = async (groupId: string | number) => {
    await remove(groupId);
    setConfirmOpen(null);
    handleCloseContextMenu();
  };


  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
      <div className="px-8 py-6 flex items-center justify-between max-lg:flex-row max-xl:items-baseline max-xl:flex-col max-lg:px-2 max-container">
        <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
          Groups
        </h1>
          <button
          onClick={() => navigate('/admin-groups/create')}
            className="id-card-button-primary px-4 py-2 rounded-full bg-[#831843]"
          >
          Create Group
          </button>
        </div>

      <div className="px-2 pb-6 max-md:px-2 max-container">
        <div className="bg-white-overlay big-corner p-6 max-md:p-0 max-md:bg-transparent">
          <div className="flex justify-between items-center mb-6 max-xl:flex-col-reverse max-xl:items-baseline max-xl:gap-4">
            <div className="flex gap-4 w-[40%] max-xl:w-[60%] max-md:w-full">
              <div className="flex-1 relative">
          <input
                  type="text"
                  placeholder="Search by name or host..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-4 py-3 pl-8 h-10 border rounded-lg focus:outline-none focus:ring-2 id-card-input"
                />
                <img src="/icons/Search.svg" alt="search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" />
              </div>
              {/* <button onClick={() => loadList()} className="px-6 py-2 rounded-full bg-secondary-bg text-white hover:bg-opacity-90 transition-colors">Search</button> */}
          </div>
          </div>

          <div className="hidden 1xl:block overflow-x-auto 1xl:w-[95%] 2xl:w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Settings</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      No groups found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentGroups.map((group) => (
                    <TableRow key={String(group.id)}>
                      <TableCell className="font-medium">
                        {String(group.id)}
                      </TableCell>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>{group.description || 'N/A'}</TableCell>
                      <TableCell>{group.host}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-1 rounded text-sm ${group.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {group.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {group.settings ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">settings</span>
                        ) : (
                          <span className="text-gray-400">No settings</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={e => handleContextMenuClick(e, String(group.id))}
                          className="p-2 py-3 border-2 border-gray-200 hover:bg-gray-100 rounded-full"
                        >
                          <img
                            className="min-w-[10px]"
                            src="/icons/dots.svg"
                            alt="dots"
                          />
                                </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
                              </div>

          <div className="1xl:hidden space-y-4">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : currentGroups.length === 0 ? (
              <div className="text-center py-8">No groups found</div>
            ) : (
              currentGroups.map((group) => (
                <div
                  key={String(group.id)}
                  className="bg-white rounded-[20px] border border-gray-200 p-4 shadow-sm"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-bold text-lg mb-1">{String(group.id)}</p>
                      <div className="flex items-center gap-2 max-md:gap-1">
                        <h3 className="font-semibold max-1xl:text-[#71717A] max-md:font-normal text-lg max-2xs:text-sm">
                          {group.name}
                        </h3>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm text-gray-600">
                          {group.host}
                        </span>
                          </div>
                    </div>
                    <button
                      onClick={e => handleContextMenuClick(e, String(group.id))}
                      className="p-2 border border-gray-300 hover:bg-gray-100 rounded-full"
                    >
                      <img
                        className="w-4 h-4"
                        src="/icons/dots.svg"
                        alt="dots"
                      />
                    </button>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Description:</span>
                      <span>{group.description || 'N/A'}</span>
              </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`inline-block px-2 py-1 rounded text-sm ${group.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {group.status === 1 ? 'Active' : 'Inactive'}
                      </span>
              </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Settings:</span>
                      <span>
                        {group.settings ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">settings</span>
                        ) : (
                          <span className="text-gray-400">No settings</span>
                        )}
                      </span>
                  </div>
                  </div>
                </div>
              ))
            )}
              </div>

          {!loading && currentGroups.length > 0 && (
            <div className="flex justify-center mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() =>
                        handlePageChange(Math.max(1, currentPage - 1))
                      }
                      className={
                        currentPage === 1
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>

                  {Array.from(
                    { length: Math.min(totalPages, 20) },
                    (_, i) => i + 1
                  ).map(page => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => handlePageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  {totalPages > 20 && (
                    <>
                      <PaginationItem>
                        <span className="px-3 py-2">...</span>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => handlePageChange(totalPages)}
                          className="cursor-pointer"
                        >
                          {totalPages}
                        </PaginationLink>
                      </PaginationItem>
                    </>
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        handlePageChange(
                          Math.min(totalPages, currentPage + 1)
                        )
                      }
                      className={
                        currentPage === totalPages
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
          </div>
          )}
        </div>
      </div>

      {contextMenu.isOpen && (
        <div
          className="fixed bg-white card-corner shadow-lg border border-gray-200 z-50 w-fit"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          <button
            onClick={() => handleViewGroup(contextMenu.groupId)}
            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            View
          </button>
            <button
            onClick={() => setConfirmOpen(contextMenu.groupId)}
            className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
            >
            Delete
            </button>
        </div>
      )}

      {contextMenu.isOpen && (
        <div className="fixed inset-0 z-40" onClick={handleCloseContextMenu} />
      )}

      {/* Delete Confirmation */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setConfirmOpen(null)} />
          <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Group</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this group? This action cannot be undone.</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmOpen(null)}
                className="id-card-button-secondary px-4 py-2 rounded-full"
              >
                Cancel
              </button>
                  <button
                onClick={() => handleDeleteGroup(confirmOpen)}
                className="id-card-button-primary w-full px-4 py-2 rounded-full"
                  >
                Delete
                  </button>
              </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Groups;
